import React, { useEffect, useMemo, useRef, useState } from "react";
import "../assets/css/School.css";
import banner from "../assets/img/schoolbanner/schoolofeng.webp";
import students1 from "../assets/img/engineering-students-working-in-a-laboratory.avif";
import students2 from "../assets/img/student-engineering-project.png";
import facultyBanner from "../assets/img/faculty-banner.png";
import computer from "../assets/img/computer.webp"
import civil from "../assets/img/civil.webp"
import mech from "../assets/img/mech.webp"
import ele from "../assets/img/elec.webp"

import Header from "../components/Header";
import Footer from "../components/Footer";

const programmes = [
    {
        level: "undergraduate",
        degree: "B.Tech",
        title: "Computer Science & Engineering",
        duration: "4 Years",
        eligibility: "10+2 with Physics, Mathematics and one approved subject",
        description:
            "Build strong foundations in programming, algorithms, cloud computing, artificial intelligence and modern software engineering.",
    },
    {
        level: "undergraduate",
        degree: "B.Tech",
        title: "Mechanical Engineering",
        duration: "4 Years",
        eligibility: "10+2 with Physics, Chemistry and Mathematics",
        description:
            "Explore design, manufacturing, thermodynamics, automation and sustainable engineering through practical project-based learning.",
    },
    {
        level: "undergraduate",
        degree: "B.Tech",
        title: "Civil Engineering",
        duration: "4 Years",
        eligibility: "10+2 with Physics, Chemistry and Mathematics",
        description:
            "Learn structural design, construction technology, surveying and sustainable infrastructure development.",
    },
    {
        level: "postgraduate",
        degree: "M.Tech",
        title: "Computer Science & Engineering",
        duration: "2 Years",
        eligibility: "Relevant B.E. or B.Tech degree",
        description:
            "Advance your expertise through specialist coursework, applied research and an industry-relevant dissertation.",
    },
    {
        level: "doctoral",
        degree: "Ph.D.",
        title: "Engineering & Technology",
        duration: "As per norms",
        eligibility: "Relevant postgraduate qualification",
        description:
            "Conduct original research with expert supervision, modern facilities and interdisciplinary collaboration.",
    },
];

const departments = [
    {
        number: "01",
        title: "Computer Science",
        text: "AI, machine learning, cloud technology, cybersecurity and software engineering.",
        image: computer,
    },
    {
        number: "02",
        title: "Mechanical Engineering",
        text: "Design, manufacturing, robotics, thermal engineering and industrial automation.",
        image: mech,
    },
    {
        number: "03",
        title: "Civil Engineering",
        text: "Smart infrastructure, structural engineering, construction and environmental systems.",
        image: civil,
    },
    {
        number: "04",
        title: "Electrical Engineering",
        text: "Power systems, electrical machines, renewable energy and intelligent control.",
        image: ele,
    },
];

const faculty = [
    {
        name: "Dr. Arvind Sharma",
        role: "Professor · Computer Science",
        focus: "Artificial Intelligence & Data Systems",
        image:
            "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=700&q=85",
    },
    {
        name: "Dr. Nisha Verma",
        role: "Associate Professor · Civil Engineering",
        focus: "Sustainable Infrastructure",
        image:
            "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=700&q=85",
    },
    {
        name: "Prof. Rahul Mehta",
        role: "Assistant Professor · Mechanical",
        focus: "Automation & Advanced Manufacturing",
        image:
            "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&w=700&q=85",
    },
];

const faqs = [
    {
        q: "Which programmes are offered by the School?",
        a: "The School offers undergraduate, postgraduate and doctoral programmes across Computer Science, Mechanical, Civil, Electrical and allied engineering disciplines. Replace this demo answer with your approved programme list.",
    },
    {
        q: "What is the eligibility for B.Tech admission?",
        a: "Applicants generally require 10+2 with Physics and Mathematics along with another approved subject. Final eligibility should be confirmed from the University’s current admission policy.",
    },
    {
        q: "Does the curriculum include practical laboratory work?",
        a: "Yes. Laboratory sessions, workshops, live assignments, mini-projects and a final capstone project are integrated into the learning journey.",
    },
    {
        q: "Are internships and industry interactions available?",
        a: "Students receive opportunities for internships, expert sessions, industrial visits, hackathons and collaborative projects through the School’s industry engagement initiatives.",
    },
    {
        q: "What placement support is provided?",
        a: "The career team supports students with aptitude preparation, technical training, mock interviews, résumé development and campus recruitment opportunities.",
    },
];

function Counter({ value, suffix = "" }) {
    const [count, setCount] = useState(0);
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                const duration = 1400;
                const start = performance.now();

                const animate = (now) => {
                    const progress = Math.min((now - start) / duration, 1);
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setCount(Math.round(value * eased));
                    if (progress < 1) requestAnimationFrame(animate);
                };

                requestAnimationFrame(animate);
                observer.disconnect();
            },
            { threshold: 0.45 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [value]);

    return (
        <strong ref={ref}>
            {count}
            {suffix}
        </strong>
    );
}

function SectionTitle({ eyebrow, title, accent, description, light = false }) {
    return (
        <div className={`school-heading ${light ? "school-heading--light" : ""}`}>
            <span className="school-heading__eyebrow">{eyebrow}</span>
            <h2>
                {title} <em>{accent}</em>
            </h2>
            {description && <p>{description}</p>}
        </div>
    );
}

export default function School() {
    const [programmeFilter, setProgrammeFilter] = useState("all");
    const [openFaq, setOpenFaq] = useState(0);

    const visibleProgrammes = useMemo(
        () =>
            programmeFilter === "all"
                ? programmes
                : programmes.filter((item) => item.level === programmeFilter),
        [programmeFilter]
    );

    useEffect(() => {
        const elements = document.querySelectorAll(
            ".school-page .reveal:not(.is-visible)"
        );

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    console.log(entry);

                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.08,
                rootMargin: "0px 0px -30px 0px",
            }
        );

        elements.forEach((element) => {
            observer.observe(element);
        });

        return () => {
            observer.disconnect();
        };
    }, [programmeFilter]);

    return (
        <>
            <Header />
            <main className="school-page">
                <section className="school-hero" id="overview">
                    <img
                        className="school-hero__image"
                        src={banner}
                        alt="University engineering campus"
                    />
                    <div className="school-hero__shade" />
                    <div className="school-container school-hero__content">
                        <div className="school-breadcrumb">
                            <a href="/">Home</a><span>/</span><a href="/academics">Academics</a>
                            <span>/</span><b>Engineering & Technology</b>
                        </div>
                        <span className="school-hero__label">School of</span>
                        <h1>Engineering <i>&</i><br />Technology</h1>
                        <p>
                            An innovation-led academic ecosystem where curiosity becomes
                            capability and ideas are engineered into meaningful solutions.
                        </p>
                        <div className="school-hero__actions">
                            <a className="school-button school-button--primary" href="/apply-online">
                                Apply for Admission <span>↗</span>
                            </a>
                            <a className="school-button school-button--glass" href="#programmes">
                                Explore Programmes <span>↓</span>
                            </a>
                        </div>
                    </div>

                    <div className="school-hero__stats">
                        <div><Counter value={25} suffix="+" /><span>Years of academic legacy</span></div>
                        <div><Counter value={12} suffix="+" /><span>Future-focused programmes</span></div>
                        <div><Counter value={30} suffix="+" /><span>Advanced laboratories</span></div>
                        <div><Counter value={90} suffix="%" /><span>Practical learning focus</span></div>
                    </div>
                </section>

                <nav className="school-anchor-nav" aria-label="School page navigation">
                    <div className="school-container">
                        {[
                            ["Overview", "overview"],
                            ["Programmes", "programmes"],
                            ["Departments", "departments"],
                            ["Laboratories", "laboratories"],
                            ["Research", "research"],
                            ["Faculty", "faculty"],
                            ["Placements", "placements"],
                            ["FAQ", "faq"],
                        ].map(([label, id]) => (
                            <a href={`#${id}`} key={id}>{label}</a>
                        ))}
                    </div>
                </nav>

                <section className="school-about school-section">
                    <div className="school-container school-about__grid">
                        <div className="school-about__visual reveal">
                            <div className="school-about__main-image">
                                <img src={students1} alt="Engineering students working in a laboratory"
                                />
                            </div>
                            <div className="school-about__small-image">
                                <img src={students2} alt="Student engineering project" />
                            </div>
                            <div className="school-about__seal">
                                <b>Learn</b><span>Design · Build · Lead</span>
                            </div>
                        </div>

                        <div className="school-about__copy reveal">
                            <SectionTitle
                                eyebrow="About the school"
                                title="Where knowledge meets"
                                accent="possibility."
                            />
                            <p className="school-about__lead">
                                We nurture technically strong, socially responsible and
                                future-ready professionals through rigorous academics,
                                interdisciplinary exploration and hands-on problem solving.
                            </p>
                            <p>
                                Our curriculum connects engineering fundamentals with emerging
                                technologies, industry exposure, research opportunities and
                                entrepreneurial thinking. Students learn in an environment
                                designed to encourage experimentation and collaboration.
                            </p>
                            <div className="school-about__points">
                                <span>✓ Outcome-based curriculum</span>
                                <span>✓ Industry-aligned learning</span>
                                <span>✓ Innovation and incubation</span>
                                <span>✓ Career readiness training</span>
                            </div>
                            <a className="school-text-link" href="/about-school">
                                Discover our academic vision <span>↗</span>
                            </a>
                        </div>
                    </div>
                </section>

                <section className="school-dean">
                    <div className="school-container school-dean__grid">
                        <div className="school-dean__portrait reveal">
                            <img src={facultyBanner} alt="Dean of the School of Engineering and Technology" />
                            <span>Dean · School of Engineering & Technology</span>
                        </div>
                        <blockquote className="reveal">
                            <span className="school-quote-mark">“</span>
                            <p>
                                Engineering education must do more than explain how systems
                                work. It must inspire students to imagine what could work
                                better—and give them the confidence to build it.
                            </p>
                            <footer>
                                <b>Prof. (Dr.) Your Dean Name</b>
                                <span>Ph.D. · Engineering & Technology</span>
                            </footer>
                        </blockquote>
                    </div>
                </section>

                <section className="school-programmes school-section" id="programmes">
                    <div className="school-container">
                        <div className="school-programmes__header reveal">
                            <SectionTitle
                                eyebrow="Academic programmes"
                                title="Choose a pathway."
                                accent="Create your future."
                                description="Explore programmes designed around strong foundations, emerging technologies and the skills demanded by modern industry."
                            />
                            <div className="school-filter" aria-label="Filter programmes">
                                {[
                                    ["all", "All"],
                                    ["undergraduate", "Undergraduate"],
                                    ["postgraduate", "Postgraduate"],
                                    ["doctoral", "Doctoral"],
                                ].map(([value, label]) => (
                                    <button
                                        type="button"
                                        key={value}
                                        className={programmeFilter === value ? "active" : ""}
                                        onClick={() => setProgrammeFilter(value)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="school-programme-list">
                            {visibleProgrammes.map((item, index) => (
                                <article className="school-programme reveal" key={`${item.degree}-${item.title}`}>
                                    <span className="school-programme__number">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <div className="school-programme__degree">
                                        <small>{item.level}</small><b>{item.degree}</b>
                                    </div>
                                    <div className="school-programme__copy">
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                        <div><span>Duration: {item.duration}</span><span>Eligibility: {item.eligibility}</span></div>
                                    </div>
                                    <a href="/programme-details" aria-label={`View ${item.title}`}>↗</a>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="school-departments school-section" id="departments">
                    <div className="school-container">
                        <SectionTitle
                            eyebrow="Our departments"
                            title="Specialised learning."
                            accent="Shared ambition."
                            description="Each department combines disciplinary depth with opportunities for interdisciplinary collaboration."
                        />
                        <div className="school-departments__grid">
                            {departments.map((item) => (
                                <article className="school-department-card reveal" key={item.title}>
                                    <img src={item.image} alt={`${item.title} department`} loading="lazy" />
                                    <div className="school-department-card__shade" />
                                    <span>{item.number}</span>
                                    <div>
                                        <h3>{item.title}</h3>
                                        <p>{item.text}</p>
                                        <a href="/department">Explore department ↗</a>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="school-learning school-section">
                    <div className="school-container school-learning__grid">
                        <div className="reveal">
                            <SectionTitle
                                eyebrow="The learning experience"
                                title="Learn by thinking."
                                accent="Grow by doing."
                                description="A progressive learning model that connects the classroom to laboratories, industry and the world beyond campus."
                                light
                            />
                            <div className="school-learning__steps">
                                {[
                                    ["01", "Discover", "Understand principles through guided exploration and contextual learning."],
                                    ["02", "Experiment", "Test ideas using modern laboratories, simulations and workshops."],
                                    ["03", "Create", "Translate concepts into prototypes, research and practical solutions."],
                                    ["04", "Lead", "Build communication, teamwork and professional decision-making skills."],
                                ].map(([number, title, text]) => (
                                    <article key={title}><span>{number}</span><div><h3>{title}</h3><p>{text}</p></div></article>
                                ))}
                            </div>
                        </div>
                        <div className="school-learning__image reveal">
                            <img
                                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1300&q=90"
                                alt="Students collaborating on an engineering project"
                                loading="lazy"
                            />
                            <div><strong>Project-based</strong><span>Collaborative · Applied · Relevant</span></div>
                        </div>
                    </div>
                </section>

                <section className="school-labs school-section" id="laboratories">
                    <div className="school-container">
                        <div className="school-labs__intro reveal">
                            <SectionTitle
                                eyebrow="Laboratories & facilities"
                                title="Spaces designed for"
                                accent="discovery."
                                description="Purpose-built facilities help students investigate, prototype, test and refine their ideas."
                            />
                            <a className="school-text-link" href="/facilities">View all facilities <span>↗</span></a>
                        </div>
                        <div className="school-labs__collage">
                            <figure className="school-lab school-lab--large reveal">
                                <img src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1300&q=90" alt="Advanced computing laboratory" />
                                <figcaption><b>Advanced Computing Lab</b><span>High-performance systems and specialist software</span></figcaption>
                            </figure>
                            <figure className="school-lab reveal">
                                <img src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=900&q=90" alt="Research laboratory" />
                                <figcaption><b>Research Centre</b><span>Interdisciplinary experimentation</span></figcaption>
                            </figure>
                            <figure className="school-lab reveal">
                                <img src="https://images.unsplash.com/photo-1581093458791-9d42e3c2a42b?auto=format&fit=crop&w=900&q=90" alt="Engineering workshop" />
                                <figcaption><b>Engineering Workshop</b><span>Fabrication and prototyping</span></figcaption>
                            </figure>
                        </div>
                    </div>
                </section>

                <section className="school-research school-section" id="research">
                    <div className="school-container school-research__grid">
                        <div className="school-research__copy reveal">
                            <SectionTitle
                                eyebrow="Research & innovation"
                                title="Ideas with the power"
                                accent="to make an impact."
                                description="Students and faculty work together on research that responds to contemporary technological, industrial and social challenges."
                                light
                            />
                            <div className="school-research__metrics">
                                <div><Counter value={120} suffix="+" /><span>Research publications</span></div>
                                <div><Counter value={18} suffix="+" /><span>Patents and innovations</span></div>
                                <div><Counter value={35} suffix="+" /><span>Active projects</span></div>
                            </div>
                            <a className="school-button school-button--primary" href="/research">Explore research <span>↗</span></a>
                        </div>
                        <div className="school-research__areas reveal">
                            <small>Priority research areas</small>
                            {["Artificial Intelligence", "Sustainable Infrastructure", "Smart Manufacturing", "Clean Energy Systems"].map((item, index) => (
                                <a href="/research" key={item}><span>0{index + 1}</span><b>{item}</b><i>↗</i></a>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="school-faculty school-section" id="faculty">
                    <div className="school-container">
                        <div className="school-faculty__header reveal">
                            <SectionTitle
                                eyebrow="Faculty"
                                title="Experienced educators."
                                accent="Dedicated mentors."
                                description="Meet academics who bring together subject expertise, research insight and a commitment to student growth."
                            />
                            <a className="school-text-link" href="/faculty">Meet all faculty <span>↗</span></a>
                        </div>
                        <div className="school-faculty__grid">
                            {faculty.map((person) => (
                                <article className="school-faculty-card reveal" key={person.name}>
                                    <div><img src={person.image} alt={person.name} loading="lazy" /><a href="/faculty-profile">↗</a></div>
                                    <small>{person.role}</small><h3>{person.name}</h3><p>{person.focus}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="school-careers school-section" id="placements">
                    <div className="school-container school-careers__grid">
                        <div className="school-careers__image reveal">
                            <img
                                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1400&q=90"
                                alt="Students preparing for professional careers"
                                loading="lazy"
                            />
                            <span>From campus<br />to career.</span>
                        </div>
                        <div className="school-careers__copy reveal">
                            <SectionTitle
                                eyebrow="Careers & placements"
                                title="Prepared for opportunity."
                                accent="Ready for impact."
                                description="Career preparation begins early through technical training, communication development, internships, mentoring and employer engagement."
                            />
                            <div className="school-careers__stats">
                                <div><Counter value={250} suffix="+" /><span>Recruiting partners</span></div>
                                <div><Counter value={95} suffix="%" /><span>Placement assistance</span></div>
                                <div><Counter value={45} suffix=" LPA" /><span>Highest package*</span></div>
                                <div><Counter value={6500} suffix="+" /><span>Successful placements*</span></div>
                            </div>
                            <p className="school-note">*Replace demo statistics with verified current placement data.</p>
                            <a className="school-button school-button--dark" href="/placements">Explore placements <span>↗</span></a>
                        </div>
                    </div>
                </section>

                <section className="school-student-story">
                    <div className="school-container school-student-story__grid">
                        <div className="reveal">
                            <span>Student experience</span>
                            <blockquote>
                                “The freedom to experiment, supportive faculty and real project
                                exposure helped me become more confident—not only as an engineer,
                                but also as a problem solver.”
                            </blockquote>
                            <b>Student Name · B.Tech Computer Science</b>
                        </div>
                        <img
                            className="reveal"
                            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=90"
                            alt="University students on campus"
                            loading="lazy"
                        />
                    </div>
                </section>

                <section className="school-faq school-section" id="faq">
                    <div className="school-container school-faq__grid">
                        <div className="reveal">
                            <SectionTitle
                                eyebrow="Frequently asked questions"
                                title="Everything you need"
                                accent="to know."
                                description="Find quick answers about programmes, eligibility, facilities and student opportunities."
                            />
                            <a className="school-text-link" href="/contact">Still have a question? Contact us <span>↗</span></a>
                        </div>
                        <div className="school-faq__list reveal">
                            {faqs.map((item, index) => {
                                const isOpen = openFaq === index;
                                return (
                                    <article className={`school-faq__item ${isOpen ? "open" : ""}`} key={item.q}>
                                        <button
                                            type="button"
                                            aria-expanded={isOpen}
                                            onClick={() => setOpenFaq(isOpen ? -1 : index)}
                                        >
                                            <span>{String(index + 1).padStart(2, "0")}</span>
                                            <b>{item.q}</b><i>{isOpen ? "−" : "+"}</i>
                                        </button>
                                        <div><p>{item.a}</p></div>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="school-admission">
                    <div className="school-admission__orb school-admission__orb--one" />
                    <div className="school-admission__orb school-admission__orb--two" />
                    <div className="school-container reveal">
                        <span>Admissions open</span>
                        <h2>Build what comes <em>next.</em></h2>
                        <p>Start your journey with the School of Engineering & Technology.</p>
                        <div>
                            <a className="school-button school-button--primary" href="/apply-online">Apply now <span>↗</span></a>
                            <a className="school-button school-button--glass" href="/download-brochure">Download brochure <span>↓</span></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
